import { Alert, Button, Checkbox, Field, Modal } from '@catering/ui';
import { type FormEvent, type JSX, useState } from 'react';

import { ZONES, ZONE_LABELS, type Zone } from '../../../../customers/api';
import type { OperatingUnitOption } from '../../../../operating-units/operating-units-api';
import type { InviteUserInput, RoleOption } from '../../../../users/api';
import { seesAllOperatingUnits } from '../../../../users/operating-unit-scope';
import { OperatingUnitsFieldset } from './OperatingUnitsFieldset';

export interface UserInviteModalProps {
  roles: RoleOption[];
  actorPermissions: string[];
  unitOptions?: OperatingUnitOption[];
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (input: InviteUserInput) => void;
}

const CANNOT_GRANT_REASON = 'No podés otorgar este rol: no tenés todos sus permisos';

/** A role's permission union exceeds the actor's own — `assertCanGrantRoles`, server-side. */
function cannotGrant(role: RoleOption, actorPermissions: string[]): boolean {
  const actorSet = new Set(actorPermissions);
  return role.permissions.some((code) => !actorSet.has(code));
}

/**
 * `internal-users/spec.md` → invitation; `design.md` §9.4: client-side
 * mirror of `assertCanGrantRoles`, UX only — the server remains the
 * authority.
 */
export function UserInviteModal({
  roles,
  actorPermissions,
  unitOptions = [],
  submitting,
  formError,
  onCancel,
  onSubmit,
}: UserInviteModalProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [operatingUnitIds, setOperatingUnitIds] = useState<string[]>([]);

  function toggleRole(roleId: string): void {
    setRoleIds((current) =>
      current.includes(roleId) ? current.filter((id) => id !== roleId) : [...current, roleId],
    );
  }

  function toggleZone(zone: Zone): void {
    setZones((current) =>
      current.includes(zone) ? current.filter((z) => z !== zone) : [...current, zone],
    );
  }

  function toggleOperatingUnit(id: string): void {
    setOperatingUnitIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({
      email,
      fullName,
      roleIds,
      zones,
      ...(operatingUnitIds.length === 0 ? {} : { operatingUnitIds }),
    });
  }

  return (
    <Modal open onClose={onCancel} title="Invitar usuario" className="max-w-lg">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Email"
          name="email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Field
          label="Nombre completo"
          name="fullName"
          required
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Roles</legend>
          {roles.map((role) => {
            const disabled = cannotGrant(role, actorPermissions);

            return (
              <Checkbox
                key={role.id}
                label={role.name}
                checked={roleIds.includes(role.id)}
                onChange={() => toggleRole(role.id)}
                disabled={disabled}
                title={disabled ? CANNOT_GRANT_REASON : undefined}
              />
            );
          })}
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Zonas</legend>
          {ZONES.map((zone) => (
            <Checkbox
              key={zone}
              label={ZONE_LABELS[zone]}
              checked={zones.includes(zone)}
              onChange={() => toggleZone(zone)}
            />
          ))}
          <p className="text-xs text-slate-500">Si no marcás ninguna, trabaja con todas las zonas.</p>
        </fieldset>

        <OperatingUnitsFieldset
          options={unitOptions}
          selectedIds={operatingUnitIds}
          onToggle={toggleOperatingUnit}
        />

        {seesAllOperatingUnits(
          {
            roleCodes: roles.filter((role) => roleIds.includes(role.id)).map((role) => role.code),
            operatingUnits: operatingUnitIds.map((id) => ({ id, name: id })),
          },
          unitOptions.length,
        ) ? (
          <Alert tone="warning">Ve todos los puntos</Alert>
        ) : null}

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting} disabled={roleIds.length === 0}>
            Invitar
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
